import * as React from 'react';
import { AlertTriangle, CheckCircle2, Info, X, XCircle, type LucideIcon } from 'lucide-react';
import { cn } from '../../lib/cn';

export type BannerTone = 'info' | 'success' | 'warning' | 'danger' | 'accent';

const toneMap: Record<BannerTone, { wrap: string; icon: string; Icon: LucideIcon }> = {
  info: { wrap: 'bg-info-soft border-info', icon: 'text-info', Icon: Info },
  success: { wrap: 'bg-success-soft border-success', icon: 'text-success', Icon: CheckCircle2 },
  warning: { wrap: 'bg-warning-soft border-warning', icon: 'text-warning', Icon: AlertTriangle },
  danger: { wrap: 'bg-danger-soft border-danger', icon: 'text-danger', Icon: XCircle },
  accent: { wrap: 'bg-accent-soft border-accent', icon: 'text-accent', Icon: Info },
};

export interface BannerProps {
  tone?: BannerTone;
  title?: React.ReactNode;
  children?: React.ReactNode;
  action?: React.ReactNode;
  icon?: boolean;
  onClose?: () => void;
  className?: string;
}

/** Inline alert with tone, optional icon, action, and close. */
export function Banner({ tone = 'info', title, children, action, icon = true, onClose, className }: BannerProps) {
  const { wrap, icon: iconColor, Icon } = toneMap[tone];
  return (
    <div
      role="status"
      className={cn('flex gap-3 rounded-card border p-3 px-[var(--space-3-5)] text-fg', wrap, className)}
    >
      {icon ? <Icon className={cn('mt-px size-[18px] shrink-0', iconColor)} strokeWidth={2} /> : null}
      <div className="min-w-0 flex-1">
        {title ? (
          <div className={cn('text-base font-semibold', children ? 'mb-0.5' : '')}>{title}</div>
        ) : null}
        {children ? <div className="text-sm leading-snug text-fg-secondary">{children}</div> : null}
        {action ? <div className="mt-2">{action}</div> : null}
      </div>
      {onClose ? (
        <button
          type="button"
          aria-label="닫기"
          onClick={onClose}
          className="shrink-0 cursor-pointer p-0.5 text-fg-muted hover:text-fg"
        >
          <X className="size-4" />
        </button>
      ) : null}
    </div>
  );
}
